import { AppCard, AppPage } from '../../components/general';
import { FC, useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Button } from 'primereact/button';
import { DataTableSearchCreateHeader } from '../../components/datatable';
import { useNavigate } from 'react-router-dom';
import {
  FeeConfigurationViewModel,
  FeeHudLineViewModel,
  FeeRulesVersionViewModel,
  FeeConfigGroupViewModel,
  CreateOrUpdateFeeConfigGroupViewModel,
  ExcelImportResult,
} from '../../models/fees';
import { confirmPopup, ConfirmPopup } from 'primereact/confirmpopup';
import { Dropdown } from 'primereact/dropdown';
import { Tooltip } from 'primereact/tooltip';
import { PublishVersionDialog } from './PublishVersionDialog';
import { ImportFeeConfigurationsDialog } from './ImportFeeConfigurationsDialog';
import { MigrationDialog } from './MigrationDialog';
import { FeeConfigGroupTree } from './FeeConfigGroupTree';
import { GroupNameDialog } from './GroupNameDialog';
import { GroupLogicDrawer } from './GroupLogicDrawer';
import { MoveToGroupDialog } from './MoveToGroupDialog';

interface FeesProps {
  feeConfigurations: FeeConfigurationViewModel[];
  hudLines: FeeHudLineViewModel[];
  versions: FeeRulesVersionViewModel[];
  groups: FeeConfigGroupViewModel[];
  selectedVersionId: number | null;
  onVersionChange: (versionId: number | null) => void;
  onPublish: (description: string, publishDate?: string | null) => Promise<void>;
  onDeleteVersion: (id: number) => Promise<void>;
  draftConfigCount: number;
  draftHudLineCount: number;
  onImport: (file: File) => Promise<ExcelImportResult>;
  onMigrationExport: () => Promise<void>;
  onMigrationPrepareImport: (file: File) => Promise<void>;
  onDelete: (id: number) => void;
  onCreateGroup: (payload: CreateOrUpdateFeeConfigGroupViewModel) => Promise<FeeConfigGroupViewModel>;
  onUpdateGroup: (args: { id: number; payload: CreateOrUpdateFeeConfigGroupViewModel }) => Promise<void>;
  onDeleteGroup: (id: number) => Promise<void>;
  onMoveConfigs: (args: { configIds: number[]; targetGroupId: number | null }) => Promise<void>;
}

const toGroupPayload = (
  group: FeeConfigGroupViewModel,
  overrides: Partial<CreateOrUpdateFeeConfigGroupViewModel>
): CreateOrUpdateFeeConfigGroupViewModel => ({
  parentFeeConfigGroupId: group.parentFeeConfigGroupId,
  name: group.name,
  qualifyingExpression: group.qualifyingExpression,
  sortOrder: group.sortOrder,
  ...overrides,
});

export const Fees: FC<FeesProps> = ({
  feeConfigurations,
  hudLines,
  versions,
  groups,
  selectedVersionId,
  onVersionChange,
  onPublish,
  onDeleteVersion,
  draftConfigCount,
  draftHudLineCount,
  onImport,
  onMigrationExport,
  onMigrationPrepareImport,
  onDelete,
  onCreateGroup,
  onUpdateGroup,
  onDeleteGroup,
  onMoveConfigs,
}) => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const [selectedHudLineId, setSelectedHudLineId] = useState<number | null>(null);
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [isCompactView, setIsCompactView] = useState(false);
  const [showPublishDialog, setShowPublishDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showMigrationDialog, setShowMigrationDialog] = useState(false);
  const filterButtonRef = useRef<HTMLDivElement>(null);
  const filterMenuRef = useRef<HTMLDivElement>(null);

  const [selectedConfigIds, setSelectedConfigIds] = useState<Set<number>>(new Set());
  const [collapsedGroupIds, setCollapsedGroupIds] = useState<Set<number>>(new Set());
  const [ungroupedCollapsed, setUngroupedCollapsed] = useState(false);

  const [groupNameDialog, setGroupNameDialog] = useState<
    { mode: 'create'; parent: FeeConfigGroupViewModel | null } | { mode: 'rename'; group: FeeConfigGroupViewModel } | null
  >(null);
  const [logicDrawerGroup, setLogicDrawerGroup] = useState<FeeConfigGroupViewModel | null>(null);
  const [moveDialogConfigIds, setMoveDialogConfigIds] = useState<number[] | null>(null);

  const isDraft = selectedVersionId === null;

  // Search filtering walks every group, so debounce the value that drives the tree; the input
  // itself stays responsive because DataTableSearchCreateHeader holds its own text state.
  const handleSearchChange = useCallback((value: string) => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => setSearch(value), 250);
  }, []);

  useEffect(() => () => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
  }, []);

  useEffect(() => {
    const handleResize = () => setIsCompactView(window.innerWidth <= 1140);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setSelectedHudLineId(null);
  }, [selectedVersionId]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        filterMenuRef.current &&
        filterButtonRef.current &&
        !filterMenuRef.current.contains(target) &&
        !filterButtonRef.current.contains(target) &&
        !(target as Element).closest('.p-dropdown-panel')
      ) {
        setShowFilterMenu(false);
      }
    };

    if (showFilterMenu) {
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showFilterMenu]);

  const activeFilterCount = useMemo(() => (selectedHudLineId !== null && selectedHudLineId !== undefined ? 1 : 0), [selectedHudLineId]);

  const versionOptions = useMemo(() => {
    const options = versions
      .sort((a, b) => (b.versionNumber || 0) - (a.versionNumber || 0))
      .map((version) => ({
        label: `${version.versionNumber}${version.description ? ` - ${version.description}` : ''}`,
        value: version.feeRulesVersionId,
      }));
    return [{ label: 'Draft', value: null }, ...options];
  }, [versions]);

  const hudLineOptions = useMemo(() => {
    const options = hudLines
      .map((hudLine) => ({
        label: hudLine.hudLine ? `${hudLine.hudLine}${hudLine.description ? ` - ${hudLine.description}` : ''}` : `HUD Line ${hudLine.feeHudLineId}`,
        value: hudLine.feeHudLineId,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return [{ label: 'All HUD Lines', value: null }, ...options];
  }, [hudLines]);

  const hudLineMap = useMemo(() => new Map(hudLines.map((hudLine) => [hudLine.feeHudLineId, hudLine])), [hudLines]);

  const matchesVersion = (versionId: number | null | undefined) =>
    selectedVersionId === null ? !versionId : versionId === selectedVersionId;

  const versionGroups = useMemo(
    () => groups.filter((group) => matchesVersion(group.feeRulesVersionId)),
    [groups, selectedVersionId]
  );

  // Collapse deep subtrees by default so a large hierarchy paints only its roots and their direct
  // children. Seed once per version (when its groups first arrive); later user toggles and group
  // edits are preserved because we do not reseed for the same version.
  const seededVersionRef = useRef<number | null | undefined>(undefined);
  useEffect(() => {
    if (seededVersionRef.current === selectedVersionId || versionGroups.length === 0) return;
    setCollapsedGroupIds(new Set(versionGroups.filter((group) => group.parentFeeConfigGroupId != null).map((group) => group.feeConfigGroupId)));
    seededVersionRef.current = selectedVersionId;
  }, [versionGroups, selectedVersionId]);

  const versionConfigurations = useMemo(
    () =>
      feeConfigurations.filter((config) => {
        // Global configs (no HUD line) apply request-wide, so they stay visible under any HUD-line filter.
        const matchesHudLine =
          selectedHudLineId == null || config.feeHudLineId === selectedHudLineId || config.feeHudLineId == null;
        return matchesVersion(config.feeRulesVersionId) && matchesHudLine;
      }),
    [feeConfigurations, selectedVersionId, selectedHudLineId]
  );

  // Selection is keyed by config id; only ids still present in the current version render, so a
  // stale selection from another version is naturally excluded without an effect-based reset.
  const visibleSelectedConfigIds = useMemo(
    () => versionConfigurations.filter((config) => selectedConfigIds.has(config.feeConfigurationId)).map((config) => config.feeConfigurationId),
    [versionConfigurations, selectedConfigIds]
  );

  const totalVersionConfigCount = useMemo(
    () => feeConfigurations.filter((config) => matchesVersion(config.feeRulesVersionId)).length,
    [feeConfigurations, selectedVersionId]
  );

  const nextRootSortOrder = useMemo(() => {
    const rootOrders = versionGroups.filter((group) => group.parentFeeConfigGroupId == null).map((group) => group.sortOrder);
    return rootOrders.length ? Math.max(...rootOrders) + 1 : 0;
  }, [versionGroups]);

  const filterActive = search.trim().length > 0 || selectedHudLineId != null;

  const toggleConfigSelected = useCallback(({ configId }: { configId: number }) => {
    setSelectedConfigIds((previous) => {
      const next = new Set(previous);
      if (next.has(configId)) next.delete(configId);
      else next.add(configId);
      return next;
    });
  }, []);

  const toggleCollapse = useCallback(({ groupId }: { groupId: number }) => {
    setCollapsedGroupIds((previous) => {
      const next = new Set(previous);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  }, []);

  const navigateToConfig = useCallback(({ config }: { config: FeeConfigurationViewModel }) => {
    const versionId = config.feeRulesVersionId;
    navigate(versionId ? `/fees/edit/${config.feeConfigurationId}?versionId=${versionId}` : `/fees/edit/${config.feeConfigurationId}`);
  }, [navigate]);

  const copyConfig = useCallback(({ config }: { config: FeeConfigurationViewModel }) => {
    navigate('/fees/create', {
      state: {
        sourceConfig: {
          feeConfigurationId: config.feeConfigurationId,
          feeConfigurationGuid: config.feeConfigurationGuid,
          feeRulesVersionId: config.feeRulesVersionId,
          feeHudLineId: config.feeHudLineId,
          isGlobal: config.isGlobal,
          description: config.description,
          rules: config.rules,
        },
        // Business code, not the numeric id: a published config's feeHudLineId points at a
        // published (non-draft) HUD line row, so the copy resolves the equivalent draft line by code.
        hudLineCode: config.feeHudLineId != null ? hudLineMap.get(config.feeHudLineId)?.hudLine ?? null : null,
      },
    });
  }, [navigate, hudLineMap]);

  const handleGroupNameSubmit = async (name: string) => {
    if (!groupNameDialog) return;
    if (groupNameDialog.mode === 'create') {
      const parent = groupNameDialog.parent;
      const siblingOrders = versionGroups
        .filter((group) => (group.parentFeeConfigGroupId ?? null) === (parent?.feeConfigGroupId ?? null))
        .map((group) => group.sortOrder);
      const sortOrder = parent
        ? siblingOrders.length
          ? Math.max(...siblingOrders) + 1
          : 0
        : nextRootSortOrder;
      const created = await onCreateGroup({
        parentFeeConfigGroupId: parent?.feeConfigGroupId ?? null,
        name,
        qualifyingExpression: null,
        sortOrder,
      });
      setLogicDrawerGroup(created);
    } else {
      // Resolve the freshest group from the live query data so a rename does not revert a
      // qualifyingExpression/sortOrder/parent that changed after this dialog was opened.
      const latestGroup = groups.find((group) => group.feeConfigGroupId === groupNameDialog.group.feeConfigGroupId) ?? groupNameDialog.group;
      await onUpdateGroup({ id: latestGroup.feeConfigGroupId, payload: toGroupPayload(latestGroup, { name }) });
    }
  };

  const handleLogicSave = async (qualifyingExpression: string | null) => {
    if (!logicDrawerGroup) return;
    // Resolve the freshest group from the live query data so saving logic does not revert a
    // name/sortOrder/parent that changed after the drawer was opened.
    const latestGroup = groups.find((group) => group.feeConfigGroupId === logicDrawerGroup.feeConfigGroupId) ?? logicDrawerGroup;
    await onUpdateGroup({ id: latestGroup.feeConfigGroupId, payload: toGroupPayload(latestGroup, { qualifyingExpression }) });
  };

  const handleDeleteGroup = useCallback(({ group, target }: { group: FeeConfigGroupViewModel; target: HTMLElement }) => {
    confirmPopup({
      target,
      message: `Delete group "${group.name}"? Configurations must be moved out of the group first.`,
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger p-button-sm',
      accept: () => onDeleteGroup(group.feeConfigGroupId),
    });
  }, [onDeleteGroup]);

  const handleDeleteConfig = useCallback(({ config, target }: { config: FeeConfigurationViewModel; target: HTMLElement }) => {
    confirmPopup({
      target,
      message: 'Are you sure you want to delete this fee configuration?',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger p-button-sm',
      accept: () => onDelete(config.feeConfigurationId),
    });
  }, [onDelete]);

  const moveSingleConfig = useCallback(({ config }: { config: FeeConfigurationViewModel }) => setMoveDialogConfigIds([config.feeConfigurationId]), []);
  const addSubGroup = useCallback(({ parent }: { parent: FeeConfigGroupViewModel }) => setGroupNameDialog({ mode: 'create', parent }), []);
  const editGroupLogic = useCallback(({ group }: { group: FeeConfigGroupViewModel }) => setLogicDrawerGroup(group), []);
  const renameGroup = useCallback(({ group }: { group: FeeConfigGroupViewModel }) => setGroupNameDialog({ mode: 'rename', group }), []);
  const toggleUngrouped = useCallback(() => setUngroupedCollapsed((previous) => !previous), []);

  const handleMoveSubmit = async (targetGroupId: number | null) => {
    if (!moveDialogConfigIds) return;
    await onMoveConfigs({ configIds: moveDialogConfigIds, targetGroupId });
    setSelectedConfigIds(new Set());
  };

  // Dropdown values (group id, or -1 for ungrouped) the configs being moved already belong to,
  // so the dialog can disable the current group as a no-op target.
  const moveDialogCurrentGroupValues = useMemo(() => {
    if (!moveDialogConfigIds) return [];
    const movingIds = new Set(moveDialogConfigIds);
    const values = feeConfigurations
      .filter((config) => movingIds.has(config.feeConfigurationId))
      .map((config) => config.feeConfigGroupId ?? -1);
    return [...new Set(values)];
  }, [feeConfigurations, moveDialogConfigIds]);

  return (
    <AppPage
      title="Fees"
      breadcrumbs={[{ label: 'Dashboard', to: '/dashboard' }, { label: 'Fees', to: '/products/fees' }, { label: 'Fee Configurations' }]}
      headerEnd={
        <div className="flex items-center gap-2" style={{ alignItems: 'stretch' }}>
          <label className="text-sm text-gray-600 whitespace-nowrap flex items-center">Version:</label>
          <Dropdown
            value={selectedVersionId}
            options={versionOptions}
            optionLabel="label"
            optionValue="value"
            onChange={(e) => onVersionChange(e.value)}
            placeholder="Select Version"
            className="w-80 border-1 border-solid border-gray-300"
            appendTo="self"
          />
          <Button icon="pi pi-check" label="Publications" className="p-button-sm p-button-success" onClick={() => setShowPublishDialog(true)} />
        </div>
      }
    >
      <AppCard>
        <div className="flex justify-between mb-4">
          <div className="flex gap-2 items-center relative">
            <DataTableSearchCreateHeader createNewDisabled onSearchChange={handleSearchChange} />
            <div ref={filterButtonRef}>
              <Button
                icon="pi pi-filter"
                label="Filter"
                className="p-button-secondary"
                onClick={() => setShowFilterMenu(!showFilterMenu)}
                aria-controls="filter-menu"
                aria-haspopup
              >
                {activeFilterCount > 0 && (
                  <span className="p-badge p-component ml-2" style={{ minWidth: '1.2rem', height: '1.2rem', lineHeight: '1.2rem', fontSize: '0.75rem' }}>
                    {activeFilterCount}
                  </span>
                )}
              </Button>
            </div>
            {showFilterMenu && (
              <div
                ref={filterMenuRef}
                className="absolute top-full mt-2 bg-white border-1 border-gray-300 rounded shadow-lg z-50"
                style={{ minWidth: '320px', right: 0 }}
              >
                <div className="flex flex-column gap-3 p-3">
                  <div className="flex flex-column gap-2">
                    <label className="text-sm font-semibold">HUD Line</label>
                    <Dropdown
                      value={selectedHudLineId}
                      options={hudLineOptions}
                      optionLabel="label"
                      optionValue="value"
                      onChange={(e) => setSelectedHudLineId(e.value ?? null)}
                      placeholder="Select HUD Line"
                      className="w-full border-1 border-solid border-gray-300"
                      showClear
                      filter
                      filterPlaceholder="Search HUD Lines"
                      appendTo="self"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-2">
            <Tooltip target=".new-group-btn" />
            <Button
              icon="pi pi-sitemap"
              label={!isCompactView ? 'New Group' : undefined}
              className="p-button-sm p-button-outlined new-group-btn"
              onClick={() => setGroupNameDialog({ mode: 'create', parent: null })}
              disabled={!isDraft}
              style={{
                ...(!isDraft ? { opacity: 0.3 } : {}),
                ...(isCompactView ? { width: '2.5rem', height: '2.5rem', padding: 0 } : {}),
              }}
              tooltip={isCompactView ? 'New Group' : undefined}
              tooltipOptions={{ position: 'bottom' }}
            />
            <Tooltip target=".create-config-btn" />
            <Button
              icon="pi pi-plus"
              label={!isCompactView ? 'Create Configuration' : undefined}
              className="p-button-sm p-button-primary create-config-btn"
              onClick={() => navigate('/fees/create')}
              disabled={!isDraft}
              style={{
                ...(!isDraft ? { opacity: 0.3 } : {}),
                ...(isCompactView ? { width: '2.5rem', height: '2.5rem', padding: 0 } : {}),
              }}
              tooltip={isCompactView ? 'Create Configuration' : undefined}
              tooltipOptions={{ position: 'bottom' }}
            />
            <Tooltip target=".import-btn" />
            <Button
              icon="pi pi-upload"
              label={!isCompactView ? 'Import' : undefined}
              className="p-button-sm p-button-secondary import-btn"
              onClick={() => setShowImportDialog(true)}
              disabled={!isDraft}
              style={{
                ...(!isDraft ? { opacity: 0.3 } : {}),
                ...(isCompactView ? { width: '2.5rem', height: '2.5rem', padding: 0 } : {}),
              }}
              tooltip={isCompactView ? 'Import' : undefined}
              tooltipOptions={{ position: 'bottom' }}
            />
            <Tooltip target=".manage-hud-btn" />
            <Button
              icon="pi pi-list"
              label={!isCompactView ? 'Manage HUD Lines' : undefined}
              className="p-button-sm p-button-secondary manage-hud-btn"
              onClick={() => navigate(selectedVersionId ? `/fees/hud-lines?versionId=${selectedVersionId}` : '/fees/hud-lines')}
              style={isCompactView ? { width: '2.5rem', height: '2.5rem', padding: 0 } : undefined}
              tooltip={isCompactView ? 'Manage HUD Lines' : undefined}
              tooltipOptions={{ position: 'bottom' }}
            />
            <Tooltip target=".migration-btn" />
            <Button
              icon="pi pi-sync"
              label={!isCompactView ? 'Migration' : undefined}
              className="p-button-sm p-button-secondary migration-btn"
              onClick={() => setShowMigrationDialog(true)}
              disabled={!isDraft}
              style={{
                ...(!isDraft ? { opacity: 0.3 } : {}),
                ...(isCompactView ? { width: '2.5rem', height: '2.5rem', padding: 0 } : {}),
              }}
              tooltip={isCompactView ? 'Migration' : undefined}
              tooltipOptions={{ position: 'bottom' }}
            />
          </div>
        </div>

        {visibleSelectedConfigIds.length > 0 && (
          <div className="flex align-items-center gap-3 mb-3 p-2 border-round" style={{ background: '#eef4ff' }}>
            <span className="font-medium">{visibleSelectedConfigIds.length} selected</span>
            <Button
              icon="pi pi-arrow-right-arrow-left"
              label="Move to group"
              className="p-button-sm"
              onClick={() => setMoveDialogConfigIds(visibleSelectedConfigIds)}
            />
            <Button icon="pi pi-times" label="Clear" className="p-button-sm p-button-text" onClick={() => setSelectedConfigIds(new Set())} />
          </div>
        )}

        <FeeConfigGroupTree
          groups={versionGroups}
          configurations={versionConfigurations}
          hudLineMap={hudLineMap}
          isDraft={isDraft}
          search={search}
          filterActive={filterActive}
          selectedConfigIds={selectedConfigIds}
          collapsedGroupIds={collapsedGroupIds}
          ungroupedCollapsed={ungroupedCollapsed}
          onToggleConfigSelected={toggleConfigSelected}
          onToggleCollapse={toggleCollapse}
          onToggleUngrouped={toggleUngrouped}
          onEditConfig={navigateToConfig}
          onCopyConfig={copyConfig}
          onMoveConfig={moveSingleConfig}
          onDeleteConfig={handleDeleteConfig}
          onAddSubGroup={addSubGroup}
          onEditGroupLogic={editGroupLogic}
          onRenameGroup={renameGroup}
          onDeleteGroup={handleDeleteGroup}
        />

        <div className="mt-3 text-sm text-gray-600">
          Total: {totalVersionConfigCount} {totalVersionConfigCount === 1 ? 'configuration' : 'configurations'} &middot; {versionGroups.length}{' '}
          {versionGroups.length === 1 ? 'group' : 'groups'}
        </div>
      </AppCard>

      <ConfirmPopup />

      <GroupNameDialog
        visible={groupNameDialog !== null}
        mode={groupNameDialog?.mode ?? 'create'}
        initialName={groupNameDialog?.mode === 'rename' ? groupNameDialog.group.name : ''}
        parentName={groupNameDialog?.mode === 'create' ? groupNameDialog.parent?.name ?? null : null}
        onClose={() => setGroupNameDialog(null)}
        onSubmit={handleGroupNameSubmit}
      />

      <GroupLogicDrawer visible={logicDrawerGroup !== null} group={logicDrawerGroup} onClose={() => setLogicDrawerGroup(null)} onSave={handleLogicSave} />

      <MoveToGroupDialog
        key={moveDialogConfigIds ? moveDialogConfigIds.join(',') : 'closed'}
        visible={moveDialogConfigIds !== null}
        groups={versionGroups}
        configCount={moveDialogConfigIds?.length ?? 0}
        currentGroupValues={moveDialogCurrentGroupValues}
        onClose={() => setMoveDialogConfigIds(null)}
        onMove={handleMoveSubmit}
      />

      <PublishVersionDialog
        visible={showPublishDialog}
        onClose={() => setShowPublishDialog(false)}
        onPublish={onPublish}
        onDeleteVersion={onDeleteVersion}
        onSelectVersion={(id) => {
          onVersionChange(id);
          setShowPublishDialog(false);
        }}
        versions={versions}
        draftConfigCount={draftConfigCount}
        draftHudLineCount={draftHudLineCount}
      />
      <ImportFeeConfigurationsDialog visible={showImportDialog} onClose={() => setShowImportDialog(false)} onImport={onImport} />
      <MigrationDialog visible={showMigrationDialog} onClose={() => setShowMigrationDialog(false)} onExport={onMigrationExport} onImport={onMigrationPrepareImport} />
    </AppPage>
  );
};
